import {Field, Form, Formik} from 'formik'
import * as Yup from 'yup'

import 'flatpickr/dist/themes/airbnb.css'
import Flatpickr from 'react-flatpickr'
// import 'src/_metronic/assets/sass/core/vendors/plugins/_flatpickr.scss';

import Select from 'react-select'
import CreatableSelect from 'react-select/creatable'

import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'

import {RefObject, useRef, useState} from 'react'
import {useIntl} from 'react-intl'
import {useDeleteFile, useUploadFile} from 'src/feature/bite/context/useMutation'
import {
  useAddCategory,
  useAddFamily,
  useCreateProduct,
} from 'src/feature/product/context/useMutation'
import {useProduct} from 'src/feature/product/hook'
import {OverlayTrigger, Tooltip} from 'react-bootstrap'

export default function ProductForm(): JSX.Element {
  const intl = useIntl()
  const {family, categories, visibility} = useProduct()

  const {isLoading, mutateAsync} = useCreateProduct()
  const {isLoading: isLoadingFamily, mutateAsync: mutateAsyncFamily} = useAddFamily()
  const {isLoading: isLoadingCategory, mutateAsync: mutateAsyncCategory} = useAddCategory()

  const {isLoading: isLoadingUpload, mutateAsync: mutateAsyncUpload} = useUploadFile()
  const {isLoading: isLoadingDelete, mutateAsync: mutateAsyncDelete} = useDeleteFile()

  const [previewURL, setPreviewURL] = useState<any>(null)
  const [medias, setMedias] = useState<string[]>([])
  const [description, setDescription] = useState<string>('')

  const validSchema = Yup.object({
    productName: Yup.string()
      .required(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'}))
      .max(50, intl.formatMessage({id: 'GLOBAL.VALIDATION.MAX_LENGTH_FIELD'}, {length: 50})),
    description: Yup.string()
      .required(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'}))
      .max(500, intl.formatMessage({id: 'GLOBAL.VALIDATION.MAX_LENGTH_FIELD'}, {length: 500})),
    publishDate: Yup.string().required(
      intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'})
    ),
    endDate: Yup.string()
      .required(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'}))
      // min publis date < end date
      .test('min', intl.formatMessage({id: 'GLOBAL.VALIDATION.MIN_DATE'}), function (value) {
        const {path, createError} = this
        const {publishDate} = this.parent
        return (
          new Date(value) > new Date(publishDate) ||
          createError({
            path,
            message: intl.formatMessage({
              id: 'GLOBAL.VALIDATION.END_DATE_MUST_BE_GREATER_THAN_START_DATE',
            }),
          })
        )
      }),
    sku: Yup.string()
      .required(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'}))
      .max(25, intl.formatMessage({id: 'GLOBAL.VALIDATION.MAX_LENGTH_FIELD'}, {length: 25})),
    barcode: Yup.string()
      .required(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'}))
      .max(25, intl.formatMessage({id: 'GLOBAL.VALIDATION.MAX_LENGTH_FIELD'}, {length: 25})),
    productType: Yup.string()
      .required(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'}))
      .max(25, intl.formatMessage({id: 'GLOBAL.VALIDATION.MAX_LENGTH_FIELD'}, {length: 25})),
    purchaseTypeID: Yup.string().required(
      intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'})
    ),
    statusID: Yup.number()
      .required(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'}))
      .oneOf([1, 2], intl.formatMessage({id: 'GLOBAL.VALIDATION.INVALID_FIELD'})),
    seasonal: Yup.boolean().required(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'})),
    // image: Yup.mixed().required(intl.formatMessage({ id: 'GLOBAL.VALIDATION.REQUIRED_FIELD' })),
    // media: Yup.array(Yup.string()).notRequired(),
    // media: Yup.mixed().notRequired(),
    price: Yup.number()
      .required(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'}))
      // max 100000 min 0.1
      .test('min', intl.formatMessage({id: 'GLOBAL.ERROR.MINIMUM_VALUE'}), function (value) {
        const {path, createError} = this
        return (
          value >= 0.1 ||
          createError({
            path,
            message: intl.formatMessage({id: 'GLOBAL.ERROR.MINIMUM_VALUE'}),
          })
        )
      })
      .test('max', intl.formatMessage({id: 'GLOBAL.ERROR.MAXIMUM_VALUE'}), function (value) {
        const {path, createError} = this
        return (
          value <= 100000 ||
          createError({
            path,
            message: intl.formatMessage({id: 'GLOBAL.ERROR.MAXIMUM_VALUE'}),
          })
        )
      }),
    eligibleForPoints: Yup.boolean().required(
      intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'})
    ),
    eligibleForDiscount: Yup.boolean().required(
      intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'})
    ),
    payWithPoints: Yup.boolean().required(
      intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'})
    ),
    approvedProduct: Yup.boolean().required(
      intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'})
    ),
    productSubCategoryID: Yup.string()
      .required(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'}))
      .oneOf(
        family.map((fam) => fam.id),
        intl.formatMessage({id: 'GLOBAL.VALIDATION.INVALID_FIELD'})
      ),
    productCategoryID: Yup.string()
      .required(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'}))
      .oneOf(
        categories.map((category) => category.id),
        intl.formatMessage({id: 'GLOBAL.VALIDATION.INVALID_FIELD'})
      ),
    // visibilityList: Yup.array(Yup.string())
    //   .required(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'}))
    //   .min(1, intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'})),
  })

  const onSubmit = async (values, {setSubmitting, resetForm}) => {
    // const media = values.media.map((m) => m.id);

    mutateAsync({
      ...values,
      // approvedProduct: values.approvedProduct === 1 ? true : false,
      // eligibleForPoints: values.eligibleForPoints === 1 ? true : false,
      // payWithPoints: values.payWithPoints === 1 ? true : false,
      // image: values.image?.id || values.image,
      // media,
      description,
    })
    setSubmitting(false)
  }

  const reactQuillRef: RefObject<ReactQuill> = useRef<ReactQuill>(null)

  const checkCharacterCount = (event: any) => {
    const unprivilegedEditor = (reactQuillRef?.current as any)?.unprivilegedEditor
    console.log(unprivilegedEditor.getLength())
    if (unprivilegedEditor.getLength() > 502 && event.key !== 'Backspace') event.preventDefault()
  }

  const addCategory = async (name: string) => {
    const newCategory = await mutateAsyncCategory({name})
    return newCategory
  }

  const addFamily = async (name: string) => {
    const newFamily = await mutateAsyncFamily({name})
    return newFamily
  }

  const uploadFile = async (file: File) => {
    const data = await mutateAsyncUpload(file)
    return data
  }

  const deleteFile = async (file: {id: string; folder: string; url: string}) => {
    console.log(file)
    await mutateAsyncDelete(file)
  }

  const status = [
    {value: 1, label: intl.formatMessage({id: 'ACTIVE'})},
    {value: 2, label: intl.formatMessage({id: 'INACTIVE'})},
  ]

  const seasonal = [
    {value: true, label: intl.formatMessage({id: 'ON'})},
    {value: false, label: intl.formatMessage({id: 'OFF'})},
  ]

  const purchaseTypeSelect = [
    {value: '0', label: intl.formatMessage({id: 'PRODUCT.INPUT.AT_A_PRICE'})},
    {value: '1', label: intl.formatMessage({id: 'PRODUCT.INPUT.CALL_TO_PURCHASE'})},
  ]

  const eligibleForPointsSelect = [
    {value: true, label: intl.formatMessage({id: 'YES'})},
    {value: false, label: intl.formatMessage({id: 'NO'})},
  ]

  const payWithPointsSelect = [
    {value: true, label: intl.formatMessage({id: 'YES'})},
    {value: false, label: intl.formatMessage({id: 'NO'})},
  ]

  const approvedProductSelect = [
    {value: true, label: intl.formatMessage({id: 'YES'})},
    {value: false, label: intl.formatMessage({id: 'NO'})},
  ]

  const categorySelect = categories.map((category) => ({
    value: category.id,
    label: category.name,
  }))

  const familySelect = family.map((fam) => ({
    value: fam.id,
    label: fam.name,
  }))

  const visibilitySelect = visibility.map((vis) => ({
    value: vis.id,
    label: vis.name,
  }))

  return (
    <Formik
      // enableReinitialize
      initialValues={{
        productName: '',
        description: '',
        publishDate: new Date().toISOString(),
        endDate: new Date(new Date().setDate(new Date().getDate() + 1)).toISOString(),
        sku: '',
        barcode: '',
        productType: '',
        purchaseTypeID: '',
        statusID: 1,
        // image: '',
        // media: [],
        price: '100.00',
        seasonal: false,
        eligibleForPoints: true,
        eligibleForDiscount: true,
        payWithPoints: true,
        approvedProduct: true,
        productSubCategoryID: '',
        productCategoryID: '',
        visibilityList: [],
      }}
      validationSchema={validSchema}
      onSubmit={onSubmit}
    >
      {(prop) => {
        return (
          <Form className='form d-flex flex-column flex-lg-row fv-plugins-bootstrap5 fv-plugins-framework'>
            {/* <div className='d-flex flex-column gap-7 gap-lg-10 w-100 w-lg-300px mb-7 me-lg-10'>
              <div className='card card-flush py-4'>
                <div className='card-header'>
                  <div className='card-title'>
                    <h2>{intl.formatMessage({ id: 'PRODUCT.TAB.THUMBNAIL' })}</h2>
                  </div>
                </div>

                <div className='card-body text-center pt-0'>
                  <div
                    className='image-input image-input-empty image-input-outline image-input-placeholder mb-3'
                    data-kt-image-input='true'
                  >
                    <div className='image-input-wrapper w-150px h-150px d-flex align-items-center justify-content-center'>
                      {previewURL ? (
                        <img src={previewURL.url} alt="" style={{ height: '100%', width: '100%' }} />
                      ) : prop.values.image ? (
                        <img src={prop?.values?.image.url} alt="" style={{ height: '100%', width: '100%' }} />
                      ) : (
                        <i className='bi bi-image fs-2'></i>
                      )}
                    </div>

                    <label
                      className='btn btn-icon btn-circle btn-active-color-primary w-25px h-25px bg-body shadow'
                      data-kt-image-input-action='change'
                      data-bs-toggle='tooltip'
                      aria-label='Change avatar'
                      data-bs-original-title='Change avatar'
                      data-kt-initialized='1'
                    >
                      <i className='ki-outline ki-pencil fs-7'></i>

                      <Field
                        type='file'
                        accept='.png, .jpg, .jpeg'
                        value=""
                        onChange={async (e) => {
                          const upload = await uploadFile(e.target.files[0]);
                          const oldDelete = prop.values.image ? prop.values.image : null;

                          if (oldDelete) {
                            // deleteFile(oldDelete);
                          }

                          if (upload && upload.file) {
                            prop.setFieldValue('image', upload.file)
                            setPreviewURL(upload.file)
                          }
                        }}
                      />
                    </label>

                    <span
                      className='btn btn-icon btn-circle btn-active-color-primary w-25px h-25px bg-body shadow'
                      data-kt-image-input-action='cancel'
                      data-bs-toggle='tooltip'
                      aria-label='Cancel avatar'
                      data-bs-original-title='Cancel avatar'
                      data-kt-initialized='1'
                    >
                      <i className='ki-outline ki-cross fs-2'></i>{' '}
                    </span>

                    <span
                      className='btn btn-icon btn-circle btn-active-color-primary w-25px h-25px bg-body shadow'
                      data-kt-image-input-action='remove'
                      data-bs-toggle='tooltip'
                      aria-label='Remove avatar'
                      data-bs-original-title='Remove avatar'
                      data-kt-initialized='1'
                    >
                      <i className='ki-outline ki-cross fs-2'></i>{' '}
                    </span>

                    <div className='fv-plugins-message-container fv-plugins-message-container--enabled invalid-feedback'>
                      {prop.errors.image && prop.touched.image && (
                        <div className='fv-help-block'>
                          {prop.errors.image}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className=' fs-7'>
                    {intl.formatMessage({ id: 'GLOBAL.UPLOAD.THUMBNAIL' })}
                  </div>
                </div>
              </div>

              <div className='card card-flush py-4'>
                <div className='card-header'>
                  <div className='card-title'>
                    <h2>{intl.formatMessage({ id: 'PRODUCT.TAB.STATUS' })}</h2>
                  </div>

                  <div className='card-toolbar'>
                    <div
                      className='rounded-circle bg-success w-15px h-15px'
                      id='kt_ecommerce_add_product_status'
                    ></div>
                  </div>
                </div>

                <div className='card-body pt-0'>
                  <Select
                    className="custom-select"
                    options={status}
                    value={status.find((s) => s.value === prop.values.statusID)}
                    onChange={(value) => {
                      prop.setFieldValue('statusID', value?.value)
                    }}
                  />

                  <div className=' fs-7 mt-5'>{intl.formatMessage({ id: 'PRODUCT.INFO.STATUS' })}</div>
                </div>
              </div>

              <div className='card card-flush py-4'>
                <div className='card-header'>
                  <div className='card-title'>
                    <h2>{intl.formatMessage({ id: 'PRODUCT.TAB.CATEGORY' })}</h2>
                  </div>
                </div>

                <div className='card-body pt-0'>
                  <div className=''>
                    <div className='align-items-center mt-2'>
                      <Select
                        className="custom-select"
                        options={categorySelect}
                        value={categorySelect.find((s) => s.value === prop.values.productCategoryID)}
                        onChange={(value) => {
                          prop.setFieldValue('productCategoryID', value?.value)
                        }}
                      />
                    </div>

                    <div className='fv-plugins-message-container fv-plugins-message-container--enabled invalid-feedback'>
                      {prop.errors.productCategoryID && prop.touched.productCategoryID && (
                        <div className='fv-help-block'>
                          {prop.errors.productCategoryID}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className=' fs-7 mt-5'>{intl.formatMessage({ id: 'PRODUCT.INFO.CATEGORY' })}</div>

                  <span className='btn btn-primary btn-sm mt-5' onClick={() => setCreateNewCategory(true)}>
                    <i className='ki-outline ki-plus fs-2'></i> {intl.formatMessage({ id: 'GLOBAL.BUTTON.CREATE.CATEGORY' })}
                  </span>

                  {createNewCategory && (
                    <div className='d-flex flex-column mt-5'>
                      <label className='form-label'>{intl.formatMessage({ id: 'PRODUCT.INPUT.CATEGORY_NAME' })}</label>

                      <input
                        type='text'
                        className='form-control form-control-solid'
                        placeholder={intl.formatMessage({ id: 'PRODUCT.INPUT.CATEGORY_NAME' })}
                        value={categoryName}
                        onChange={(e) => setCategoryName(e.target.value)}
                      />

                      <div className=' fs-7 mt-2'>
                        {intl.formatMessage({ id: 'PRODUCT.INFO.ADD_CATEGORY' })}
                      </div>

                      <div className='d-flex flex-stack mt-5'>
                        <button
                          className='btn btn-light me-3'
                          onClick={() => setCreateNewCategory(false)}
                        >
                          {intl.formatMessage({ id: 'GLOBAL.BUTTON.CANCEL' })}
                        </button>

                        <button className='btn btn-primary' onClick={() => addCategory(categoryName)} disabled={isLoadingCategory}>
                          {isLoadingCategory ? intl.formatMessage({ id: 'PLEASE_WAIT' }) : intl.formatMessage({ id: 'GLOBAL.BUTTON.CREATE' })}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div> */}

            <div className='d-flex flex-column flex-row-fluid gap-7 gap-lg-12'>
              <div className='tab-content'>
                <div
                  className='tab-pane fade active show'
                  id='kt_ecommerce_add_product_general'
                  role='tabpanel'
                >
                  <div className='d-flex flex-column gap-7 gap-lg-10'>
                    <div className='card card-flush py-4'>
                      <div className='card-header d-flex flex-colum  pb-10'>
                        <div className='card-title'>
                          <h2>{intl.formatMessage({id: 'PRODUCT.TAB.GENERAL'})}</h2>
                        </div>
                        <div className='card-subtitle w-100'>
                          {intl.formatMessage({id: 'PRODUCT.TAB.GENERAL_TOOLTIP'})}
                        </div>
                      </div>

                      <div className='card-body pt-0'>
                        <div className='mb-2 fv-row fv-plugins-icon-container'>
                          <div className='row mb-5'>
                            <div className='col-12'>
                              <div className='mb-5 fv-row fv-plugins-icon-container'>
                                <label className='required form-label'>
                                  {intl.formatMessage({id: 'PRODUCT.INPUT.PRODUCT_TITLE'})}
                                </label>

                                <Field
                                  type='text'
                                  name='productName'
                                  className='form-control mb-2'
                                  placeholder={intl.formatMessage({
                                    id: 'PRODUCT.INPUT.PRODUCT_TITLE',
                                  })}
                                  maxLength={51}
                                />

                                <div className='fv-plugins-message-container fv-plugins-message-container--enabled invalid-feedback'>
                                  {prop.errors.productName && prop.touched.productName && (
                                    <div className='fv-help-block'>{prop.errors.productName}</div>
                                  )}
                                </div>
                              </div>
                            </div>

                            <div className='col-4'>
                              <div className='mb-5 fv-row fv-plugins-icon-container'>
                                <label className='required form-label'>
                                  {intl.formatMessage({id: 'PRODUCT.TAB.STATUS'})}

                                  <span className='position-relative z-50'>
                                    <OverlayTrigger
                                      placement='top'
                                      overlay={
                                        <Tooltip>
                                          {intl.formatMessage({id: 'PRODUCT.TAB.STATUS_TOOLTIP'})}
                                        </Tooltip>
                                      }
                                    >
                                      <i className='bi bi-info-circle text-primary ms-1'></i>
                                    </OverlayTrigger>
                                  </span>
                                </label>
                                <Select
                                  className='custom-select'
                                  options={status}
                                  value={status.find((s) => s.value === prop.values.statusID)}
                                  onChange={(value) => {
                                    prop.setFieldValue('statusID', value?.value)
                                  }}
                                />
                                <div className='fv-plugins-message-container fv-plugins-message-container--enabled invalid-feedback'>
                                  {prop.errors.statusID && prop.touched.statusID && (
                                    <div className='fv-help-block'>{prop.errors.statusID}</div>
                                  )}
                                </div>
                              </div>
                            </div>

                            <div className='col-4'>
                              <label className='required form-label'>
                                {intl.formatMessage({id: 'PRODUCT.INPUT.PUBLISH_DATE'})}
                              </label>

                              <Flatpickr
                                // value={new Date(prop.values.publishDate || new Date())}
                                value={
                                  !prop.values.publishDate ||
                                  prop.values.publishDate === '' ||
                                  prop.values.publishDate === '0001-01-01T00:00:00.000Z'
                                    ? new Date()
                                    : new Date(prop.values.publishDate)
                                }
                                onChange={(date) => {
                                  prop.setFieldValue('publishDate', date[0])
                                }}
                                name='publishDate'
                                className='form-control mb-2'
                                options={{dateFormat: 'm/d/Y'}}
                              />

                              <div className='fv-plugins-message-container fv-plugins-message-container--enabled invalid-feedback'>
                                {prop.errors.publishDate && prop.touched.publishDate && (
                                  <div className='fv-help-block'>{prop.errors.publishDate}</div>
                                )}
                              </div>
                            </div>

                            <div className='col-4'>
                              <label className='required form-label'>
                                {intl.formatMessage({id: 'PRODUCT.INPUT.END_DATE'})}
                              </label>

                              <Flatpickr
                                value={
                                  !prop.values.endDate ||
                                  prop.values.endDate === '' ||
                                  prop.values.endDate === '0001-01-01T00:00:00.000Z'
                                    ? new Date()
                                    : new Date(prop.values.endDate)
                                }
                                onChange={(date) => {
                                  prop.setFieldValue('endDate', date[0])
                                }}
                                name='endDate'
                                className='form-control mb-2'
                                options={{dateFormat: 'm/d/Y'}}
                                // data-enable-time
                              />

                              <div className='fv-plugins-message-container fv-plugins-message-container--enabled invalid-feedback'>
                                {prop.errors.endDate && prop.touched.endDate && (
                                  <div className='fv-help-block'>{prop.errors.endDate}</div>
                                )}
                              </div>
                            </div>
                          </div>

                          <div className='row'>
                            <div className='col-4'>
                              <div className='mb-5 fv-row fv-plugins-icon-container'>
                                <label className='required form-label'>
                                  {intl.formatMessage({id: 'PRODUCT.INPUT.SKU_ID'})}

                                  <span className='position-relative z-50'>
                                    <OverlayTrigger
                                      placement='top'
                                      overlay={
                                        <Tooltip>
                                          {intl.formatMessage({id: 'PRODUCT.INPUT.SKU_ID_TOOLTIP'})}
                                        </Tooltip>
                                      }
                                    >
                                      <i className='bi bi-info-circle text-primary ms-1'></i>
                                    </OverlayTrigger>
                                  </span>
                                </label>

                                <Field
                                  type='text'
                                  name='sku'
                                  className='form-control mb-2'
                                  placeholder={intl.formatMessage({id: 'PRODUCT.INPUT.SKU_ID'})}
                                  maxLength={26}
                                />

                                <div className='fv-plugins-message-container fv-plugins-message-container--enabled invalid-feedback'>
                                  {prop.errors.sku && prop.touched.sku && (
                                    <div className='fv-help-block'>{prop.errors.sku}</div>
                                  )}
                                </div>
                              </div>
                            </div>

                            <div className='col-4'>
                              <div className='mb-5 fv-row fv-plugins-icon-container'>
                                <label className='required form-label'>
                                  {intl.formatMessage({id: 'PRODUCT.INPUT.BARCODE'})}
                                </label>

                                <Field
                                  type='text'
                                  name='barcode'
                                  className='form-control mb-2'
                                  placeholder={intl.formatMessage({id: 'PRODUCT.INPUT.BARCODE'})}
                                  maxLength={26}
                                />

                                <div className='fv-plugins-message-container fv-plugins-message-container--enabled invalid-feedback'>
                                  {prop.errors.barcode && prop.touched.barcode && (
                                    <div className='fv-help-block'>{prop.errors.barcode}</div>
                                  )}
                                </div>
                              </div>
                            </div>

                            <div className='col-4'>
                              <div className='mb-5 fv-row fv-plugins-icon-container'>
                                <label className='required form-label'>
                                  {intl.formatMessage({id: 'PRODUCT.INPUT.PRODUCT_TYPE'})}
                                </label>

                                <Field
                                  type='text'
                                  name='productType'
                                  className='form-control mb-2'
                                  placeholder={intl.formatMessage({
                                    id: 'PRODUCT.INPUT.PRODUCT_TYPE',
                                  })}
                                  maxLength={26}
                                />

                                <div className='fv-plugins-message-container fv-plugins-message-container--enabled invalid-feedback'>
                                  {prop.errors.productType && prop.touched.productType && (
                                    <div className='fv-help-block'>{prop.errors.productType}</div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className='row mt-5'>
                            <div className='col-6'>
                              <label className='required form-label'>
                                {intl.formatMessage({id: 'PRODUCT.INPUT.PURCHASE_TYPE'})}
                              </label>

                              <Select
                                className='custom-select'
                                options={purchaseTypeSelect}
                                value={purchaseTypeSelect.find(
                                  (s) => s.value === prop.values.purchaseTypeID
                                )}
                                onChange={(value) => {
                                  prop.setFieldValue('purchaseTypeID', value?.value)
                                }}
                              />

                              <div className='fv-plugins-message-container fv-plugins-message-container--enabled invalid-feedback'>
                                {prop.errors.purchaseTypeID && prop.touched.purchaseTypeID && (
                                  <div className='fv-help-block'>{prop.errors.purchaseTypeID}</div>
                                )}
                              </div>
                            </div>

                            <div className='col-4'>
                              <div className='mb-5 fv-row fv-plugins-icon-container'>
                                <label className='required form-label'>
                                  {intl.formatMessage({id: 'PRODUCT.TAB.SEASONAL'})}
                                </label>
                                <Select
                                  className='custom-select'
                                  options={seasonal}
                                  value={seasonal.find((s) => s.value === prop.values.seasonal)}
                                  onChange={(value) => {
                                    prop.setFieldValue('seasonal', value?.value)
                                    // end date + 1 year
                                    if (value?.value) {
                                      prop.setFieldValue(
                                        'endDate',
                                        new Date(
                                          new Date(prop.values.endDate).setFullYear(
                                            new Date(prop.values.endDate).getFullYear() + 100
                                          )
                                        )
                                      )
                                    }
                                  }}
                                />
                                <div className='fv-plugins-message-container fv-plugins-message-container--enabled invalid-feedback'>
                                  {prop.errors.seasonal && prop.touched.seasonal && (
                                    <div className='fv-help-block'>{prop.errors.seasonal}</div>
                                  )}
                                </div>
                              </div>
                            </div>

                            {/* <div className='col-6'>
                              <label className='required form-label'>
                                {intl.formatMessage({id: 'PRODUCT.INPUT.VISIBILITY'})}
                              </label>

                              <Select
                                isMulti
                                className='custom-select'
                                options={visibilitySelect}
                                onChange={(value) => {
                                  const values = value?.map((v) => v.value)
                                  prop.setFieldValue('visibilityList', values)
                                }}
                              />

                              <div className='fv-plugins-message-container fv-plugins-message-container--enabled invalid-feedback'>
                                {prop.errors.visibilityList && prop.touched.visibilityList && (
                                  <div className='fv-help-block'>{prop.errors.visibilityList}</div>
                                )}
                              </div>
                            </div> */}
                          </div>

                          <div className='row mt-8'>
                            <div className='fv-row fv-plugins-icon-container'>
                              <label className='required form-label'>
                                {intl.formatMessage({id: 'PRODUCT.INPUT.PRODUCT_DESCRIPTION'})}
                              </label>
                              <ReactQuill
                                theme='snow'
                                value={description}
                                ref={reactQuillRef}
                                onKeyDown={checkCharacterCount}
                                onChange={(value) => {
                                  setDescription(value)
                                  prop.setFieldValue('description', value)
                                }}
                              />

                              <div className='fv-plugins-message-container fv-plugins-message-container--enabled invalid-feedback'>
                                {prop.errors.description && (
                                  <div className='fv-help-block'>{prop.errors.description}</div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className='card card-flush py-4'>
                      <div className='card-header'>
                        <div className='card-title'>
                          <h2>{intl.formatMessage({id: 'PRODUCT.TAB.CATEGORY'})}</h2>
                        </div>
                      </div>
                      <div className='card-body'>
                        <div className='row'>
                          <div className='col-6'>
                            <div className=''>
                              <label className='required form-label'>
                                {intl.formatMessage({id: 'PRODUCT.TAB.CATEGORY'})}
                              </label>
                              <CreatableSelect
                                className='custom-select'
                                options={categorySelect}
                                value={categorySelect.find(
                                  (s) => s.value === prop.values.productCategoryID
                                )}
                                onChange={(value) => {
                                  prop.setFieldValue('productCategoryID', value?.value)
                                }}
                                isLoading={isLoadingCategory}
                                onCreateOption={async (inputValue) => {
                                  const newCategory = await addCategory(inputValue)

                                  if (newCategory) {
                                    prop.setFieldValue('productCategoryID', newCategory.id)
                                  }
                                }}
                              />

                              <div className='fv-plugins-message-container fv-plugins-message-container--enabled invalid-feedback'>
                                {prop.errors.productCategoryID &&
                                  prop.touched.productCategoryID && (
                                    <div className='fv-help-block'>
                                      {prop.errors.productCategoryID}
                                    </div>
                                  )}
                              </div>
                            </div>

                            {/* <div className=' fs-7 mt-5'>{intl.formatMessage({ id: 'PRODUCT.INFO.CATEGORY' })}</div> */}
                          </div>
                          <div className='col-6'>
                            <label className='required form-label'>
                              {intl.formatMessage({id: 'PRODUCT.INPUT.FAMILY'})}
                            </label>

                            <CreatableSelect
                              className='custom-select'
                              options={familySelect}
                              value={familySelect.find(
                                (s) => s.value === prop.values.productSubCategoryID
                              )}
                              onChange={(value) => {
                                prop.setFieldValue('productSubCategoryID', value?.value)
                              }}
                              isLoading={isLoadingFamily}
                              onCreateOption={async (inputValue) => {
                                const newCategory = await addFamily(inputValue)

                                if (newCategory) {
                                  prop.setFieldValue('productSubCategoryID', newCategory.id)
                                }
                              }}
                            />

                            <div className='fv-plugins-message-container fv-plugins-message-container--enabled invalid-feedback'>
                              {prop.errors.productSubCategoryID &&
                                prop.touched.productSubCategoryID && (
                                  <div className='fv-help-block'>
                                    {prop.errors.productSubCategoryID}
                                  </div>
                                )}
                            </div>
                          </div>
                          {/* <div className="col-6">

                            <div>
                              <span className='btn btn-primary btn-sm mt-5' onClick={() => setCreateNewCategory(true)}>
                                <i className='ki-outline ki-plus fs-2'></i> {intl.formatMessage({ id: 'GLOBAL.BUTTON.CREATE.CATEGORY' })}
                              </span>

                              {createNewCategory && (
                                <div className='d-flex flex-column mt-5'>
                                  <label className='form-label'>{intl.formatMessage({ id: 'PRODUCT.INPUT.CATEGORY_NAME' })}</label>

                                  <input
                                    type='text'
                                    className='form-control form-control-solid'
                                    placeholder={intl.formatMessage({ id: 'PRODUCT.INPUT.CATEGORY_NAME' })}
                                    value={categoryName}
                                    onChange={(e) => setCategoryName(e.target.value)}
                                  />

                                  <div className=' fs-7 mt-2'>
                                    {intl.formatMessage({ id: 'PRODUCT.INFO.ADD_CATEGORY' })}
                                  </div>

                                  <div className='d-flex flex-stack mt-5'>
                                    <button
                                      className='btn btn-light me-3'
                                      onClick={() => setCreateNewCategory(false)}
                                    >
                                      {intl.formatMessage({ id: 'GLOBAL.BUTTON.CANCEL' })}
                                    </button>

                                    <button className='btn btn-primary' onClick={() => addCategory(categoryName)} disabled={isLoadingCategory}>
                                      {isLoadingCategory ? intl.formatMessage({ id: 'PLEASE_WAIT' }) : intl.formatMessage({ id: 'GLOBAL.BUTTON.CREATE' })}
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div> */}
                        </div>
                      </div>
                    </div>
                    {/* <div className='card card-flush py-4'>
                      <div className='card-header'>
                        <div className='card-title'>
                          <h2>{intl.formatMessage({ id: 'PRODUCT.TAB.MEDIA' })}</h2>
                        </div>
                      </div>

                      <div className='card-body pt-0'>
                        <div className='fv-row mb-2'>
                          <label htmlFor="contained-button-file" style={{ display: 'block' }}>
                            <div className='dropzone dz-clickable'>
                              <div className='dz-message needsclick'>
                                <i className='ki-outline ki-file-up text-primary fs-3x'></i>

                                <div className='ms-4'>
                                  <h3 className='fs-5 fw-bold text-gray-900 mb-1'>
                                    {intl.formatMessage({ id: 'GLOBAL.BUTTON.UPLOAD' })}
                                  </h3>
                                  <span className='fs-7 fw-semibold text-gray-400'>
                                    {intl.formatMessage({ id: 'GLOBAL.INFO.UPLOAD' })}
                                  </span>
                                </div>

                                <Field
                                  name='media'
                                  style={{ display: 'none' }}
                                  id="contained-button-file"
                                  type='file'
                                  value=""
                                  accept='.png, .jpg, .jpeg'
                                  onChange={async (e) => {
                                    const old = prop.values.media ? prop.values.media : [];
                                    const upload = await uploadFile(e.target.files[0])

                                    if (upload && upload.file) {
                                      prop.setFieldValue('media', [...old, upload.file])
                                      setMedias([...medias, upload.file])
                                    }
                                  }}
                                />
                              </div>
                            </div>
                          </label>
                        </div>

                        <div className=' fs-7'>{intl.formatMessage({ id: 'PRODUCT.INFO.MEDIA' })}</div>
                        <div className="d-flex gap-4 mt-4">
                          {prop.values.media && (
                            <>
                              {prop.values.media.map((file: { id: string, url: string }, index: number) => (
                                <div
                                  key={index}
                                  style={{
                                    width: 100,
                                    height: 100,
                                    overflow: 'hidden',
                                    position: 'relative',
                                    borderRadius: 4,
                                    border: '1px solid #ebedf2',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                  }}
                                >
                                  <img src={file.url} alt="" style={{ maxWidth: 100 }} />
                                  <div
                                    style={{
                                      position: 'absolute',
                                      top: 0,
                                      right: 0,
                                      backgroundColor: 'rgba(255,255,255,0.5)',
                                      cursor: 'pointer',
                                      padding: 4,
                                    }}
                                    onClick={() => {
                                      const old = prop.values.media ? prop.values.media : [];
                                      const newMedia = old.filter((media) => media.id !== file.id)
                                      prop.setFieldValue('media', newMedia);
                                    }}
                                  >
                                    x
                                  </div>
                                </div>
                              ))}
                            </>
                          )}
                        </div>
                      </div>
                    </div> */}

                    <div className='card card-flush py-4'>
                      <div className='card-header'>
                        <div className='card-title'>
                          <h2>{intl.formatMessage({id: 'PRODUCT.TAB.PRICING'})}</h2>
                        </div>
                      </div>

                      <div className='card-body pt-0 w-100'>
                        <div className='row'>
                          <div className='col-4'>
                            <div className='mb-10 fv-row fv-plugins-icon-container'>
                              <label className='required form-label'>
                                {intl.formatMessage({id: 'PRODUCT.INPUT.PRICE'})}
                              </label>

                              <Field
                                type='text'
                                name='price'
                                className='form-control mb-2'
                                placeholder={intl.formatMessage({id: 'PRODUCT.INPUT.PRICE'})}
                                onKeyPress={(event) => {
                                  if (!/[0-9.,]/.test(event.key)) {
                                    event.preventDefault()
                                  }
                                }}
                                maxLength={11}
                              />

                              <div className=' fs-7'>
                                {intl.formatMessage({id: 'PRODUCT.INFO.PRICE'})}
                              </div>

                              <div className='fv-plugins-message-container fv-plugins-message-container--enabled invalid-feedback'>
                                {prop.errors.price && prop.touched.price && (
                                  <div className='fv-help-block'>{prop.errors.price}</div>
                                )}
                              </div>
                            </div>
                          </div>

                          <div className='col-4'>
                            <div className='mb-10 fv-row fv-plugins-icon-container'>
                              <label className='required form-label'>
                                {intl.formatMessage({id: 'PRODUCT.INPUT.ELIGIBLE_FOR_POINTS'})}
                              </label>

                              {/* <Field
                                as="select"
                                name='eligibleForPoints'
                                className='form-control form-select form-select-solid'
                                data-control='select2'
                                data-placeholder={intl.formatMessage({ id: 'GLOBAL.PLACEHOLDER.SELECT' })}
                                data-allow-clear='false'
                              >
                                <option value='0'>{intl.formatMessage({ id: 'YES' })}</option>
                                <option value='1'>{intl.formatMessage({ id: 'NO' })}</option>
                              </Field> */}

                              <Select
                                className='custom-select'
                                options={eligibleForPointsSelect}
                                value={eligibleForPointsSelect.find(
                                  (s) => s.value === prop.values.eligibleForPoints
                                )}
                                onChange={(value) => {
                                  prop.setFieldValue('eligibleForPoints', value?.value)
                                }}
                              />

                              <div className='fv-plugins-message-container fv-plugins-message-container--enabled invalid-feedback'>
                                {prop.errors.eligibleForPoints &&
                                  prop.touched.eligibleForPoints && (
                                    <div className='fv-help-block'>
                                      {prop.errors.eligibleForPoints}
                                    </div>
                                  )}
                              </div>
                            </div>
                          </div>

                          <div className='col-4'>
                            <div className='mb-10 fv-row fv-plugins-icon-container'>
                              <label className='required form-label'>
                                {intl.formatMessage({id: 'PRODUCT.INPUT.ELIGIBLE_FOR_DISCOUNT'})}
                              </label>

                              {/* <Field
                                as="select"
                                name='eligibleForPoints'
                                className='form-control form-select form-select-solid'
                                data-control='select2'
                                data-placeholder={intl.formatMessage({ id: 'GLOBAL.PLACEHOLDER.SELECT' })}
                                data-allow-clear='false'
                              >
                                <option value='0'>{intl.formatMessage({ id: 'YES' })}</option>
                                <option value='1'>{intl.formatMessage({ id: 'NO' })}</option>
                              </Field> */}

                              <Select
                                className='custom-select'
                                options={eligibleForPointsSelect}
                                value={eligibleForPointsSelect.find(
                                  (s) => s.value === prop.values.eligibleForDiscount
                                )}
                                onChange={(value) => {
                                  prop.setFieldValue('eligibleForDiscount', value?.value)
                                }}
                              />

                              <div className='fv-plugins-message-container fv-plugins-message-container--enabled invalid-feedback'>
                                {prop.errors.eligibleForDiscount &&
                                  prop.touched.eligibleForDiscount && (
                                    <div className='fv-help-block'>
                                      {prop.errors.eligibleForDiscount}
                                    </div>
                                  )}
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* <div
                          className='d-none mb-10 fv-row'
                          id='kt_ecommerce_add_product_discount_fixed'
                        >
                          <label className='form-label'>
                            {intl.formatMessage({id: 'PRODUCT.INPUT.FIXED_DISCOUNTED_PRICE'})}
                          </label>

                          <Field
                            type='text'
                            name='eligibleForPoints'
                            className='form-control mb-2'
                            placeholder={intl.formatMessage({
                              id: 'PRODUCT.INPUT.FIXED_DISCOUNTED_PRICE',
                            })}
                          />
                        </div> */}

                        <div className='row'>
                          <div className='col-4 fv-plugins-icon-container'>
                            <label className='required form-label'>
                              {intl.formatMessage({id: 'PRODUCT.INPUT.PAY_WITH_POINTS'})}
                            </label>

                            {/* <Field
                              as="select"
                              name='payWithPoints'
                              className='form-control form-select form-select-solid mt-5'
                              data-control='select2'
                              data-placeholder={intl.formatMessage({ id: 'GLOBAL.PLACEHOLDER.SELECT' })}
                              data-allow-clear='false'
                            >
                              <option value='0'>{intl.formatMessage({ id: 'YES' })}</option>
                              <option value='1'>{intl.formatMessage({ id: 'NO' })}</option>
                            </Field> */}

                            <Select
                              className='custom-select'
                              options={payWithPointsSelect}
                              value={payWithPointsSelect.find(
                                (s) => s.value === prop.values.payWithPoints
                              )}
                              onChange={(value) => {
                                prop.setFieldValue('payWithPoints', value?.value)
                              }}
                            />

                            <div className='fv-plugins-message-container fv-plugins-message-container--enabled invalid-feedback'>
                              {prop.errors.payWithPoints && prop.touched.payWithPoints && (
                                <div className='fv-help-block'>{prop.errors.payWithPoints}</div>
                              )}
                            </div>
                          </div>

                          <div className='col-4'>
                            <label className='form-label'>
                              {intl.formatMessage({id: 'PRODUCT.INPUT.APPROVED_PRODUCT'})}

                              <span className='position-relative z-50'>
                                <OverlayTrigger
                                  placement='top'
                                  overlay={
                                    <Tooltip>
                                      {intl.formatMessage({
                                        id: 'PRODUCT.INPUT.APPROVED_PRODUCT_TOOLTIP',
                                      })}
                                    </Tooltip>
                                  }
                                >
                                  <i className='bi bi-info-circle text-primary ms-1'></i>
                                </OverlayTrigger>
                              </span>
                            </label>

                            {/* <Field
                              as="select"
                              name='approvedProduct'
                              className='form-control form-select form-select-solid mt-5'
                              data-control='select2'
                              data-placeholder={intl.formatMessage({ id: 'GLOBAL.PLACEHOLDER.SELECT' })}
                              data-allow-clear='false'
                            >
                              <option value='0'>{intl.formatMessage({ id: 'YES' })}</option>
                              <option value='1'>{intl.formatMessage({ id: 'NO' })}</option>
                            </Field> */}

                            <Select
                              className='custom-select'
                              options={approvedProductSelect}
                              value={approvedProductSelect.find(
                                (s) => s.value === prop.values.approvedProduct
                              )}
                              onChange={(value) => {
                                prop.setFieldValue('approvedProduct', value?.value)
                              }}
                            />

                            <div className='fv-plugins-message-container fv-plugins-message-container--enabled invalid-feedback'>
                              {prop.errors.approvedProduct && prop.touched.approvedProduct && (
                                <div className='fv-help-block'>{prop.errors.approvedProduct}</div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className='d-flex justify-content-end px-10'>
                        {/* <a
                          href='../../demo31/dist/products.html'
                          id='kt_ecommerce_add_product_cancel'
                          className='btn btn-light me-5'
                        >
                          Cancel
                        </a> */}

                        <button type='submit' className='btn btn-primary'>
                          {isLoading ? (
                            <span className='indicator-label'>
                              {intl.formatMessage({id: 'PLEASE_WAIT'})}{' '}
                              <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                            </span>
                          ) : (
                            <span className='indicator-label'>
                              {intl.formatMessage({id: 'GLOBAL.BUTTON.CREATE'})}
                            </span>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Form>
        )
      }}
    </Formik>
  )
}
